import Link from "next/link";
import { ArrowRight, Plus } from "@/components/icons";

interface SupportResultBase {
  key: string;
  title: string;
  description?: string;
}

export type SupportResultLink = SupportResultBase & { href: string; answer?: never };
export type SupportQuickAnswer = SupportResultBase & { answer: string; href?: never };
export type SupportResultItem = SupportResultLink | SupportQuickAnswer;

export interface SupportResultGroup {
  label: string;
  items: SupportResultItem[];
}

export function SupportSearchResults({
  groups,
  isSearching,
}: {
  groups: SupportResultGroup[];
  isSearching: boolean;
}) {
  const visibleGroups = groups.filter((group) => group.items.length > 0);
  if (visibleGroups.length === 0) {
    return (
      <p className="px-5 py-4 text-sm text-muted-foreground">
        {isSearching
          ? "Searching our guides…"
          : "No matches. Press Enter to ask."}
      </p>
    );
  }
  return (
    <div className="divide-y divide-border">
      {visibleGroups.map((group) => (
        <section
          key={group.label}
          aria-label={group.label}
          className="px-2 py-2"
        >
          <h3 className="px-3 pb-1 pt-2 text-overline uppercase text-muted-foreground">
            {group.label}
          </h3>
          <ul>
            {group.items.map((item) => (
              <li key={item.key}>
                {item.answer === undefined ? (
                  <ResultLink item={item} />
                ) : (
                  <QuickAnswer title={item.title} answer={item.answer} />
                )}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function QuickAnswer({ title, answer }: { title: string; answer: string }) {
  return (
    <details className="group rounded-md px-3 py-2.5 open:bg-muted">
      <summary className="flex cursor-pointer list-none items-center gap-4 text-sm font-medium text-foreground [&::-webkit-details-marker]:hidden">
        <span className="min-w-0 flex-1">{title}</span>
        <Plus className="size-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-45 motion-reduce:transition-none" />
      </summary>
      <p className="pt-2 text-sm leading-relaxed text-muted-foreground">
        {answer}
      </p>
    </details>
  );
}

function ResultLink({ item }: { item: SupportResultLink }) {
  return (
    <Link
      href={item.href}
      className="group flex items-center gap-4 rounded-md px-3 py-2.5 transition-colors hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground">{item.title}</p>
        {item.description && (
          <p className="truncate text-xs text-muted-foreground">
            {item.description}
          </p>
        )}
      </div>
      <ArrowRight className="size-4 shrink-0 text-muted-foreground group-hover:text-foreground" />
    </Link>
  );
}
